import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { createStructuredSelector } from 'reselect';
import Grid from '@material-ui/core/Grid';
import IconButton from '@material-ui/core/IconButton';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import SettingsIcon from '@material-ui/icons/Settings';
import AppContainer from 'components/AppContainer';
import useSiteCopySelector from 'components/useSiteCopySelector';
import {
  isAssessmentSaved,
  getColorsConfig,
} from 'containers/Assessments/utils';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { getContentfulEntryUrl } from 'utils/contentfulService';
import MixPanel from 'utils/mixpanelService';
import {
  makeSelectAllowSignIn,
  makeSelectClientDetails,
  makeSelectTextDirection,
} from 'containers/Main/selectors';
import { makeSelectSavedAssessments } from 'containers/Assessments/selectors';
import { makeSelectAuth } from 'containers/Auth/selectors';
import { trackFavoriteAction } from 'containers/Favorites/utils';
import { saveAssessment } from 'containers/Assessments/actions';
import withSizes from 'react-sizes';
import RichTextReactRenderer from 'components/RichTextReactRenderer';
import PageIntroBlock from 'components/PageIntroBlock';
import ReactTooltip from 'react-tooltip';
import ScheduleIcon from '@material-ui/icons/Schedule';
import Button from '@material-ui/core/Button';
import ContentBlock from 'components/ContentBlock';
import {
  CONTENT_BLOCK_POSITIONS,
  CONTENT_BLOCK_TYPES,
} from 'components/ContentBlock/utils';
import useIntros from 'components/Hooks/useIntros';
import { getAssessmentName } from 'containers/Assessment/utils';
import HeartFullIcon from 'containers/Assessment/images/favorite-full.svg';
import { toSentenceCase } from 'utils/stringUtils';
import HeartEmptyIcon from './icons/HeartEmpty';

const useStyles = makeStyles(theme => ({
  topContainer: {
    paddingTop: 20,
    paddingBottom: 40,
    backgroundColor: theme.palette.bgColor.default,
    position: 'relative',
    overflow: 'visible',
    color: theme.palette.text.tertiary,
  },
  contentContainer: {
    display: 'flex',
    width: '100%',
    position: 'relative',
  },
  mainContentWrapper: {
    flex: 1,
    display: 'flex',
    justifyContent: 'space-between',
  },
  sideBar: {
    backgroundColor: '#0095DA',
    marginRight: 24,
    flexShrink: 0,
    position: 'absolute',
    left: ({ isMobile }) => (isMobile ? -20 : -40),
    top: 0,
    bottom: -110,
    height: 'auto',
    width: ({ isMobile }) => (isMobile ? 6 : 16),
    [theme.breakpoints.down('xs')]: {
      left: -15,
      width: 6,
    },
  },
  mainContent: {
    flex: 1,
  },
  rightContent: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'flex-start',
    paddingTop: 20,
  },
  badgeContainer: {
    display: 'flex',
    alignItems: 'flex-start',
  },
  badge: {
    color: theme.palette.text.tertiary,
  },
  button: {
    padding: theme.spacing(1),
    marginLeft: 16,
    backgroundColor: 'transparent',
    '&:hover': {
      background: 'none',
    },
    border: 0,
    borderRadius: 0,
  },
  settings: {
    fontSize: 25,
  },
  title: {
    ...theme.typography.h2,
    color: theme.palette.text.tertiary,
    direction: ({ textDirection }) => textDirection,
  },
  introText: {
    '& > div': {
      width: '100%',
      '& p': {
        fontSize: 17,
        textAlign: 'left',
      },
    },
    '&::before': {
      content: "''",
    },
    '& a': {
      color: 'white',
      '&:hover': {
        color: 'white',
      },
    },
  },
  timeWrapper: {
    display: 'flex',
    alignItems: 'center',
    '& > svg': {
      width: 20,
      height: 20,
      marginRight: 8,
      fill: theme.palette.text.tertiary,
    },
  },
  time: {
    color: theme.palette.text.tertiary,
    height: 20,
  },

  saveAsText: {
    color: theme.palette.text.tertiary,
    fontSize: 16,
    fontWeight: 600,
    marginLeft: 5,
  },
  icon: {
    display: 'flex',
    fontSize: 20,
    '& > div': {
      display: 'flex',
      marginLeft: 8,
    },
    '& > div > svg > g > path': {
      fill: theme.palette.primary.dark,
    },
  },
  shareAndLikeWrapper: {
    background: 'transparent',
    height: 39,
    borderRadius: 3,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    margin: '0 1px',
    maxWidth: 'none',
    width: 'auto',
    '& button': {
      marginLeft: 0,
    },
  },
  favoriteButton: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    borderRadius: 30,
    width: 'auto',
    whiteSpace: 'nowrap',
    border: `1px solid ${theme.palette.text.tertiary}`,
    backgroundColor: 'transparent',
    color: theme.palette.text.tertiary,
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
    },
  },
  favoriteText: {
    textTransform: 'none',
    fontSize: 16,
    fontWeight: 600,
    fontFamily: 'Open Sans',
    '&::first-letter': {
      textTransform: 'uppercase',
    },
  },
  favoriteIcon: {
    width: 20,
    marginRight: 8,
    fill: theme.palette.text.tertiary,
  },
  topIcon: {
    position: 'absolute',
    right: '5%',
  },
  mobileView: {
    paddingRight: '5%',
    paddingLeft: '5%',
  },
  iconBtn: {
    '&:hover': {
      background: 'none',
    },
  },
  startButton: {
    boxShadow: 'none',
    padding: '10px 22px',
    borderRadius: 32,

    border: '1px solid white',
    color: 'white',
    '& span': {
      textTransform: 'none',
      fontSize: 18,
      fontWeight: 600,
      lineHeight: '20px',
    },
    '&:hover': {
      backgroundColor: 'white',
      color: theme.palette.primary.main,
      boxShadow: 'none',
    },
  },
  startButtonContainer: {
    '&.sticky': {
      position: 'sticky',
      zIndex: 1,
      backgroundColor: theme.palette.bgColor.default,
      width: '100%',
    },
  },
  contentBlockItem: {
    padding: 0,
    '&[data-style="Basic Content"]': {
      marginTop: '22px !important',
      '& > div': {
        padding: '0px',

        '& div': {
          padding: 0,
        },

        '& div > h5': {
          fontSize: '1.125rem',
          marginBottom: 10,
        },

        '& div p': {
          fontSize: 17,
        },
      },

      '&[data-color="Default White"] > div': {
        padding: 0,
        backgroundColor: 'transparent',
      },
    },
  },
}));

const stateSelector = createStructuredSelector({
  allowSignIn: makeSelectAllowSignIn(),
  auth: makeSelectAuth(),
  clientDetails: makeSelectClientDetails(),
  savedAssessments: makeSelectSavedAssessments(),
  textDirection: makeSelectTextDirection(),
});

const CustomHeader = ({
  isAdmin,
  isMobile,
  onStartButtonClick,
  assessment,
  showFavorite = true,
}) => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [assessmentSiteCopy] = useSiteCopySelector(['assessment']);
  const {
    allowSignIn,
    auth,
    clientDetails,
    savedAssessments,
    textDirection,
  } = useSelector(stateSelector);
  const startButtonRef = useRef(null);
  const clipContainerRef = useRef(null);
  const blockRef = useRef(null);
  const classes = useStyles({
    textDirection,
    isMobile,
  });
  const isDescriptionExpanded = _get(
    clientDetails,
    'metadata.assessmentsDescriptionExpanded',
    false,
  );

  // Check if intros exist before rendering PageIntroBlock
  const assessmentId = _get(assessment, 'items.0.sys.id');
  const { data: intros } = useIntros({
    type: 'reference',
    page: 'assessments',
    referenceId: assessmentId,
    queryOptions: {
      enabled: !!assessmentId, // Only fetch if we have an ID
    },
  });
  const hasIntros = !_isEmpty(intros);

  useEffect(() => {
    if (!isMobile) {
      return undefined;
    }

    const handler = () => {
      const appHeader = document.getElementById('app-header');
      const startButtonContainer = startButtonRef.current;
      const clipContainer = clipContainerRef.current;
      const referenceBlock = blockRef.current;
      const startButtonRect = startButtonContainer.getBoundingClientRect();
      const referenceBlockRect = referenceBlock.getBoundingClientRect();

      const isButtonUnderHeader =
        referenceBlockRect.top <= appHeader.offsetHeight;

      if (isButtonUnderHeader) {
        startButtonContainer.classList.add('sticky');
        startButtonContainer.style.top = `${appHeader.offsetHeight}px`;
        clipContainer.classList.add('visible');
        clipContainer.style.top = `${Math.max(
          startButtonRect.bottom,
          appHeader.offsetHeight,
        )}px`;
      } else {
        clipContainer.classList.remove('visible');
        startButtonContainer.classList.remove('sticky');
      }
    };

    window.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, [isMobile]);

  const handleReadMoreLess = isMore => {
    const {
      fields: { name },
      sys: { id },
    } = assessment.items[0];

    MixPanel.track('Resource Page - Read More', {
      name,
      type: 'assessment',
      more: isMore,
      entryId: id,
    });
  };

  const handleSaveAssessment = (
    assessmentID,
    slug,
    name,
    time,
    callToAction,
    saved,
  ) => {
    let nodeID;
    if (saved)
      [{ nodeID }] = savedAssessments.filter(el => el.sys.id === assessmentID);

    const data = {
      nodeID,
      assessment: {
        assessmentID,
        slug,
        name,
        callToAction: callToAction || '',
        time: time || '',
      },
      save: !saved,
    };

    trackFavoriteAction(
      'Assessment Page',
      {
        assessmentID,
        slug,
        name,
      },
      auth,
      saved,
    );

    dispatch(saveAssessment(data));
  };

  if (_isEmpty(assessment)) return null;
  const {
    fields: { name, customNames, intro, time, callToAction, slug },
    sys: { id },
  } = assessment.items[0];
  const { color } = getColorsConfig(assessment.items[0]);
  const saved = isAssessmentSaved(savedAssessments, assessment.items[0]);
  const minuteLabel =
    time > 1
      ? _get(assessmentSiteCopy, 'pageCopy.minutePlural')
      : _get(assessmentSiteCopy, 'pageCopy.minuteSingular');

  const finalName = getAssessmentName(
    name,
    _get(customNames, 'config', []),
    clientDetails.shortName,
  );

  return (
    <div className={classes.topContainer}>
      <AppContainer spacing={0}>
        <div className={classes.contentContainer}>
          <div className={classes.sideBar} />
          <div className={classes.mainContentWrapper}>
            <div className={classes.mainContent}>
              <Grid
                item
                xs={12}
                sm={allowSignIn ? 7 : 'auto'}
                md={allowSignIn ? 8 : 'auto'}
                className={isMobile ? classes.mobileView : null}
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography
                      variant="h1"
                      className={classes.title}
                      align="left"
                    >
                      {finalName}
                    </Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <div className={classes.timeWrapper}>
                      <ScheduleIcon />
                      <Typography variant="overline" className={classes.time}>
                        {`${_get(
                          assessmentSiteCopy,
                          'pageCopy.takesOnlyLabel',
                        )} ${time} ${minuteLabel}`}
                      </Typography>
                    </div>
                  </Grid>
                  {isMobile && (
                    <Grid
                      item
                      xs={12}
                      className={classes.startButtonContainer}
                      ref={startButtonRef}
                    >
                      <Button
                        className={classes.startButton}
                        size="large"
                        onClick={onStartButtonClick}
                      >
                        <Typography variant="button">
                          {toSentenceCase(
                            _get(
                              assessmentSiteCopy,
                              'pageCopy.startAssessmentLabel',
                            ),
                          )}
                        </Typography>
                      </Button>
                    </Grid>
                  )}
                  {isMobile && (
                    <>
                      <div
                        ref={clipContainerRef}
                        className={classes.clipContainer}
                      />
                      <div ref={blockRef} />
                    </>
                  )}
                  {hasIntros && (
                    <PageIntroBlock
                      type="reference"
                      slug={slug}
                      page="assessments"
                      color={color.font}
                    />
                  )}
                  <Grid
                    item
                    xs={12}
                    className={classes.introText}
                    id="intro-text"
                  >
                    <RichTextReactRenderer
                      color={theme.palette.text.tertiary}
                      limit={isDescriptionExpanded ? undefined : 255}
                      data={intro}
                      readMoreLessHandler={handleReadMoreLess}
                    />
                  </Grid>
                </Grid>
                <ContentBlock
                  type="item-page"
                  id={_get(assessment, 'items.0.sys.id')}
                  position={CONTENT_BLOCK_POSITIONS.BELOW_HERO_INTRO}
                  classes={{
                    item: classes.contentBlockItem,
                  }}
                  excludedTypes={[CONTENT_BLOCK_TYPES.ACCORDION]}
                  marginBottom={false}
                />
              </Grid>
            </div>

            {allowSignIn && (
              <div className={classes.rightContent}>
                <Grid container justifyContent="flex-end" alignItems="center">
                  {allowSignIn && showFavorite && (
                    <Grid item className={classes.shareAndLikeWrapper}>
                      {isMobile ? (
                        <IconButton
                          classes={{
                            root: classes.button,
                          }}
                          onClick={() =>
                            handleSaveAssessment(
                              id,
                              slug,
                              name,
                              time,
                              callToAction,
                              saved,
                            )
                          }
                          disableRipple
                          aria-label={saved ? 'Unsave' : 'Save As Favorite'}
                        >
                          {saved ? (
                            <img
                              src={HeartFullIcon}
                              alt="Unsave"
                              className={classes.favorite}
                            />
                          ) : (
                            <HeartEmptyIcon className={classes.favoriteIcon} />
                          )}
                        </IconButton>
                      ) : (
                        <Button
                          className={classes.favoriteButton}
                          onClick={() =>
                            handleSaveAssessment(
                              id,
                              slug,
                              name,
                              time,
                              callToAction,
                              saved,
                            )
                          }
                          disableRipple
                          aria-label={saved ? 'Unsave' : 'Save As Favorite'}
                        >
                          {saved ? (
                            <img
                              src={HeartFullIcon}
                              alt=""
                              className={classes.favoriteIcon}
                            />
                          ) : (
                            <HeartEmptyIcon className={classes.favoriteIcon} />
                          )}
                          <Typography className={classes.favoriteText}>
                            {saved
                              ? _get(
                                  assessmentSiteCopy,
                                  'pageCopy.removeFromFavoritesLabel',
                                )
                              : _get(
                                  assessmentSiteCopy,
                                  'pageCopy.saveAsFavoriteLabel',
                                )}
                          </Typography>
                        </Button>
                      )}
                    </Grid>
                  )}
                  {isAdmin && (
                    <>
                      <ReactTooltip
                        id="ContentfulSetting"
                        place="top"
                        type="light"
                        border
                      >
                        See this entry on Contentful
                      </ReactTooltip>
                      <IconButton
                        classes={{ root: classes.iconBtn }}
                        disableRipple
                        aria-label="Setting"
                        data-tip
                        data-for="ContentfulSetting"
                      >
                        <a
                          href={getContentfulEntryUrl(id)}
                          target="_blank"
                          rel="noopener"
                          style={{
                            display: 'inline-flex',
                            color: 'white',
                          }}
                          aria-label="Open settings in a new tab"
                        >
                          <SettingsIcon className={classes.settings} />
                        </a>
                      </IconButton>
                    </>
                  )}
                </Grid>
              </div>
            )}
          </div>
        </div>
      </AppContainer>
    </div>
  );
};

CustomHeader.propTypes = {
  isAdmin: PropTypes.bool,
  isMobile: PropTypes.bool,
  onStartButtonClick: PropTypes.func,
};

const mapSizesToProps = ({ width }) => ({
  isMobile: width < 600,
});

export default withSizes(mapSizesToProps)(CustomHeader);
